import React, { useState, useEffect } from 'react';
import { Stack, TextField } from '@mui/material';
import { Controller, FieldErrors, Control } from 'react-hook-form';
import { translate as t } from 'hooks/useTranslations';
import { FieldOption } from '../../_types/fieldbank';
import SingleSelectOptionManager from '../../../_components/SingleSelectOptionManager';
import { BaseSelectInput } from '../../../_components/FieldInputs';

interface SingleSelectFieldProps {
  control: Control<any>;
  errors: FieldErrors<any>;
  getValues?: any;
  setValue?: any;
  fieldId?: number;
  fieldType: 'SingleSelect' | 'MultiSelect';
  isEditing?: boolean;
  isDraft?: boolean;
}

const SingleSelectField: React.FC<SingleSelectFieldProps> = ({
  control,
  errors,
  getValues,
  setValue,
  fieldId,
  fieldType,
  isEditing = false,
  isDraft = true,
}) => {
  const isMulti = fieldType === 'MultiSelect';
  const [fieldOptions, setFieldOptions] = useState<FieldOption[]>([
    { label: '', status: 'Active', isVisible: true },
    { label: '', status: 'Active', isVisible: true },
  ]);

  // Initialize options from form data
  useEffect(() => {
    const formValues = getValues();
    if (formValues?.fieldOptions && Array.isArray(formValues.fieldOptions)) {
      const existingOptions = formValues.fieldOptions.map((option: FieldOption) => ({
        // Preserve all data as received from backend for existing options
        ...option,
        label: option.label || '',
        status: option.status || 'Active',
        isVisible: option.isVisible !== false, // Default to true for field bank
      }));
      setFieldOptions(
        existingOptions.length >= 2
          ? existingOptions
          : [
              ...existingOptions,
              ...Array(2 - existingOptions.length)
                .fill(0)
                .map(() => ({
                  id: null, // New options get id: null
                  label: '',
                  status: 'Active' as 'Active',
                  isVisible: true,
                })),
            ],
      );
    }
  }, [getValues]);

  // Update form values when options change
  useEffect(() => {
    if (setValue) {
      setValue('fieldOptions', fieldOptions);
    }
  }, [fieldOptions, setValue]);

  const handleOptionsChange = (newOptions: FieldOption[]) => {
    const currentDefaultValue = getValues('defaultValues');
    const validLabels = newOptions
      .filter((opt) => opt.label && opt.label.trim() !== '' && opt.status === 'Active' && opt.isVisible !== false)
      .map((opt) => opt.label);
    if (isMulti && Array.isArray(currentDefaultValue)) {
      const filtered = currentDefaultValue.filter((v) => validLabels.includes(v));
      if (filtered.length !== currentDefaultValue.length) {
        setValue('defaultValues', filtered);
      }
    } else {
      const single = Array.isArray(currentDefaultValue) ? currentDefaultValue[0] : currentDefaultValue;
      if (single && !validLabels.includes(single)) {
        setValue('defaultValues', isMulti ? [] : '');
      }
    }
    setFieldOptions(newOptions);
  };

  // Get available options for default value dropdown (only active and visible options with labels)
  const availableOptionsForDefault = fieldOptions.filter(
    (opt) => opt.label && opt.label.trim() !== '' && opt.status === 'Active' && opt.isVisible !== false,
  );

  return (
    <Stack spacing={3}>
      {/* Field Label */}
      <Controller
        name='label'
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('performanceReporting.fieldBank.form.label')}
            required
            fullWidth
            error={!!errors.label}
            helperText={(errors.label?.message as string) || ''}
          />
        )}
      />

      {/* Options Management using shared component */}
      <SingleSelectOptionManager
        options={fieldOptions}
        onChange={handleOptionsChange}
        mode='fieldBank'
        errors={errors}
        isDraft={isDraft}
      />

      {/* Default Value Selection */}
      <Controller
        name='defaultValues'
        control={control}
        render={({ field }) => {
          const value = isMulti
            ? (Array.isArray(field.value) ? field.value : [])
            : (Array.isArray(field.value) ? field.value[0] : field.value) ?? '';
          return (
            <BaseSelectInput
              options={availableOptionsForDefault.map((opt) => ({ value: opt.label, label: opt.label }))}
              value={value}
              onChange={(newValue) => {
                if (isMulti && Array.isArray(newValue)) {
                  field.onChange(newValue);
                } else if (!isMulti) {
                  field.onChange(typeof newValue === 'string' ? newValue : '');
                } else {
                  field.onChange([]);
                }
              }}
              isMulti={isMulti}
              label={t('performanceReporting.fieldBank.form.defaultValue')}
              placeholder='Choose default value'
              size='small'
              fullWidth={true}
              isClearable={true}
              error={errors.defaultValues?.message as string | undefined}
              helperText={errors.defaultValues?.message as string | undefined}
            />
          );
        }}
      />
    </Stack>
  );
};

export default SingleSelectField;
